/**
* BudgetHero - Concentric rings layout, no outer card, 3 stat cards
*/
import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, BarChart3, Settings, Lock, Info } from "lucide-react";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { formatCurrency, formatCurrencyWhole, formatCurrencyCompact } from "../../utils/formatters"; // w2_04
import { usePurple } from "../../utils/purple";
import PlanPath from "../common/PlanPath"; // plan_11
import { usePlan } from "../../utils/plan"; // plan_07

const BudgetHero = ({
  theme = 'pink',
  viewMonth,
  onPrevMonth,
  onNextMonth,
  totalIncome,
  totalSpent,
  totalSavings,
  monthlyBudget,
  budgetAvailable,
  cashAvailable,
  cameIn, // dark_07: this month's income without interest
  carryIn = 0, // dark_07: leftover from last month
  leftToBudget,
  plannedWithdrawals = 0,
  transactionCount,
  showYearlyStats,
  onToggleYearlyStats,
  onNotesClick,
  onStatsClick,
  onOverviewClick,
  budgetSpentPercent: passedBudgetSpentPercent,
  spentExcludingExcluded,
  budgetForSpentCalc,
  onInfoClick,
  showAddMenu,
  setShowAddMenu,
  addFormType,
  setAddFormType,
  showBudgetSetup,
  // Opens the Set up <month> sheet. Given a handler, the hero shows the
  // month-start banner above the tiles for any month with no income set.
  onMonthSetup,
  userDebts = [],
  warmColors = {},
  colorMap = {},
  cards = []
}) => {
  const { hasAccess, showFeaturePaywall } = useSubscription();
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';

  const [showMoneyTooltip, setShowMoneyTooltip] = useState(false);
  const moneyTooltipRef = useRef(null);
  useEffect(() => {
    if (!showMoneyTooltip) return;
    const handler = (e) => {
      if (moneyTooltipRef.current && !moneyTooltipRef.current.contains(e.target)) {
        setShowMoneyTooltip(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showMoneyTooltip]);

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const shortMonthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const currentMonthName = monthNames[viewMonth.getMonth()];
  const shortMonthName = shortMonthNames[viewMonth.getMonth()];
  const currentYear = viewMonth.getFullYear();

  const today = new Date();
  const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
  const isCurrentMonth = viewMonth.getMonth() === today.getMonth() && viewMonth.getFullYear() === today.getFullYear();
  const isFutureMonth = viewMonth.getFullYear() > today.getFullYear() || (viewMonth.getFullYear() === today.getFullYear() && viewMonth.getMonth() > today.getMonth());
  const currentDay = isCurrentMonth ? today.getDate() : isFutureMonth ? 0 : daysInMonth;
  const monthProgress = Math.round((currentDay / daysInMonth) * 100);

  const budgetSpentPercent = passedBudgetSpentPercent !== undefined
    ? passedBudgetSpentPercent
    : (monthlyBudget > 0 ? Math.min(100, Math.round((totalSpent / monthlyBudget) * 100)) : 0);

  const leftover = totalIncome - totalSpent - totalSavings;

  // The banner is driven by the state of the month, not by the date: any month
  // with no income set still needs setting up, whether that is the 1st or the
  // 20th, this month or one you paged back to. It clears itself the moment
  // income lands - there is nothing to dismiss and nothing to remember.
  const showSetupBanner = !!onMonthSetup && !(monthlyBudget > 0);

  const accentColor = isPink ? '#e879a0' : isWarm ? '#B07D50' : '#818cf8';
  const pillActiveGradient = isPink
    ? 'linear-gradient(135deg, #F5A8C1, #E27BA1)'
    : isWarm
    ? 'linear-gradient(135deg, #B08D5B, #7C5C3A)'
    : 'linear-gradient(135deg, #6366f1, #4361EE)';
  const budgetRingColor = isPink ? '#818cf8' : isWarm ? '#2D5A3D' : '#e879a0';
  const labelColor = isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
  const goodColor = '#2E9E7B';

  // Theme-aware tile palettes — white tile bodies, icon square keeps tint, value text uses 2-stop gradient
  // Budget/Money (indices 4/5) are set to the SAME token objects as Income/Saved (0/2), not
  // separate colours — so any future tile-colour change propagates to Available automatically.
  const tilePalettes = (() => {
    const base = {
      // order: 0 Income, 1 Spent, 2 Saved/Inv, 3 Leftover
      pink: [
        { bg: 'linear-gradient(135deg, #FAE3EC 0%, #F5D5E3 100%)', label: '#C23A72', value: '#C23A72' }, // Income
        { bg: 'linear-gradient(135deg, #DBE7F5 0%, #C4D6ED 100%)', label: '#3C6693', value: '#3C6693' }, // Spent
        { bg: 'linear-gradient(135deg, #E7F1DB 0%, #D3E5BE 100%)', label: '#5C7A4A', value: '#5C7A4A' }, // Saved/Inv
        { bg: 'linear-gradient(135deg, #E9E1F5 0%, #D5C7EB 100%)', label: '#7A5FB0', value: '#7A5FB0' }, // Leftover
      ],

      blue: [
        { bg: 'linear-gradient(135deg, #E1E8FA 0%, #D2DEF5 100%)', label: '#3A56C2', value: '#3A56C2' }, // Income
        { bg: 'linear-gradient(135deg, #E6E6F5 0%, #D6D6ED 100%)', label: '#5B4F93', value: '#5B4F93' }, // Spent
        { bg: 'linear-gradient(135deg, #DCEFF0 0%, #C9E4E6 100%)', label: '#2F7A80', value: '#2F7A80' }, // Saved/Inv
        { bg: 'linear-gradient(135deg, #E4E1F5 0%, #D2CDEB 100%)', label: '#6B5FB0', value: '#6B5FB0' }, // Leftover
      ],

      warm: [
        { bg: 'linear-gradient(135deg, #F7E7D9 0%, #F0D8C2 100%)', label: '#B5651D', value: '#B5651D' }, // Income
        { bg: 'linear-gradient(135deg, #EDE3D5 0%, #E2D3BC 100%)', label: '#7A6A4A', value: '#7A6A4A' }, // Spent
        { bg: 'linear-gradient(135deg, #E9EFD9 0%, #DCE6C1 100%)', label: '#5C7A4A', value: '#5C7A4A' }, // Saved/Inv
        { bg: 'linear-gradient(135deg, #F0E3D9 0%, #E5D0C2 100%)', label: '#9A5B3C', value: '#9A5B3C' }, // Leftover
      ]
    };
    const out = {};
    for (const key of Object.keys(base)) {
      out[key] = [...base[key], base[key][0], base[key][2]]; // 4: Budget = Income token, 5: Money = Saved/Inv token
    }
    // Money's text colour reads too muted/sage against pink's warm palette — give pink a
    // brighter, more "regular" green for the Money label/value specifically (matches the
    // green already used elsewhere in the app for positive pace states), while keeping the
    // same soft background wash as Saved/Inv so the icon chip still reads as part of that family.
    out.pink[5] = { ...out.pink[5], label: '#16A34A', value: '#16A34A' };
    return out;
  })();
  const themeKey = isPink ? 'pink' : isWarm ? 'warm' : 'blue';
  const paceTrack = 'rgba(255,255,255,0.55)';
  const tilePalette = tilePalettes[themeKey];
  // Purple (admin-only): the pace card is a white card with a pink and a
  // lilac ring, like the mockup.
  const purple = usePurple();
  const plan = usePlan();
  const pace = purple
    ? { bg: '#FFFFFF', track: '#F3F0F6', month: '#D9CFEA', spent: '#8B5CE0', day: '#1E1630', line: '#F1EDF4', label: '#857D93', value: '#1E1630' }
    : { bg: tilePalette[0].bg, track: null, month: null, spent: tilePalette[0].value, day: tilePalette[0].value, line: 'rgba(255,255,255,0.6)' };
  const paceMonthRing = tilePalette[3].value;

  // Pace verdict — text + colour always agree
  const paceDelta = budgetSpentPercent - monthProgress; // negative = ahead, positive = behind
  let paceVerdict, paceTextColor, paceBgColor, paceDotColor;
  if (budgetSpentPercent >= 100) {
    paceVerdict = 'Over budget';
    paceTextColor = '#B91C1C'; paceBgColor = '#FEF2F2'; paceDotColor = '#DC2626';
  } else if (paceDelta > 15) {
    paceVerdict = 'Over pace';
    paceTextColor = '#B91C1C'; paceBgColor = '#FEF2F2'; paceDotColor = '#DC2626';
  } else if (paceDelta > 5) {
    paceVerdict = 'Slow down';
    paceTextColor = '#B45309'; paceBgColor = '#FFFBEB'; paceDotColor = '#D97706';
  } else if (paceDelta < -5) {
    paceVerdict = 'Ahead';
    paceTextColor = '#15803D'; paceBgColor = '#F0FDF4'; paceDotColor = '#16A34A';
  } else {
    paceVerdict = 'On pace';
    paceTextColor = '#15803D'; paceBgColor = '#F0FDF4'; paceDotColor = '#16A34A';
  }

  const paceBarGradient = isPink
    ? 'linear-gradient(90deg, #FF3CAC, #a855f7, #6366f1)'
    : isWarm
    ? 'linear-gradient(90deg, #7C5C3A, #B07D50, #7C9070)'
    : 'linear-gradient(90deg, #4C3BCF, #6366f1, #38b8c8)';

  // plan_07: Money plan - the mockup's top of Budget. (plan_09: calmer)
  if (plan) {
    const card = { background: '#FFFFFF', borderRadius: 24, padding: '18px', marginTop: 12 }; // plan_08: smaller
    const segBtn = (on) => ({
      flex: 1, border: 0, borderRadius: 999, padding: '8px 4px', fontSize: 13.5,
      fontWeight: on ? 600 : 500, color: on ? '#26252E' : '#6C6879',
      background: on ? '#FFFFFF' : 'transparent',
      boxShadow: on ? '0 1px 3px rgba(60,40,120,0.08)' : 'none',
    });
    const ring = (pct, track, fill) => {
      const c = 2 * Math.PI * 21;
      const v = Math.min(100, Math.max(0, pct || 0));
      return (
        <svg viewBox="0 0 52 52" style={{ width: 42, height: 42, flex: 'none', transform: 'rotate(-90deg)' }} aria-hidden="true">
          <circle cx="26" cy="26" r="21" fill="none" stroke={track} strokeWidth="7" />
          {v > 0 && <circle cx="26" cy="26" r="21" fill="none" stroke={fill} strokeWidth="7" strokeLinecap="round" strokeDasharray={`${(c * v) / 100} ${c}`} />}
        </svg>
      );
    };
    const good = paceVerdict === 'On pace' || paceVerdict === 'Ahead';
    const verdictColor = good ? '#2F8756' : '#C04B7C';
    const whole = (n) => (n < 0 ? '−' : '') + formatCurrencyWhole(Math.abs(n || 0));
    // w2_04: big amounts are shortened on the path (e.g. NGN 1.5M)
    const compact = (n) => (n < 0 ? '−' : '') + formatCurrencyCompact(Math.abs(n || 0));
    // dark_07: interest goes straight into its account, so it isn't counted
    // as money that came in to spend. Kept = came in - spent - saved.
    const income = cameIn == null ? totalIncome : cameIn;
    const kept = income - totalSpent - totalSavings;
    const cashLeft = kept + (carryIn || 0);
    const hasCarry = Math.abs(carryIn || 0) >= 0.005;
    const prevMonthName = monthNames[(viewMonth.getMonth() + 11) % 12];
    const tileRaw = [income, totalSpent, totalSavings, kept];
    const tiles = [
      { bg: '#DFF2E7', ink: '#2F8756', emoji: '💰', label: 'Income', value: whole(income) },
      { bg: '#DFE9F9', ink: '#3C6DB5', emoji: '🛍️', label: 'Spent', value: whole(totalSpent) },
      { bg: '#ECE6FA', ink: '#7A5DD3', emoji: '🐷', label: 'Saved & invested', value: whole(totalSavings) },
      { bg: '#FAE4ED', ink: '#C04B7C', emoji: '👛', label: 'Kept', value: whole(kept), note: null },
    ];
    const statsOn = showYearlyStats;
    const memosOn = showBudgetSetup;
    const monthOn = !memosOn && !statsOn;
    return (
      <div className="px-4 pt-4 pb-2 wy-budget-hero" data-testid="plan-budget-hero">
        <div className="flex items-center justify-between" style={{ gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="flex items-center" style={{ gap: 8 }}>{/* plan_27 */}
              <h1 style={{ margin: 0 }}>your budget</h1>{/* plan_28 */}
              <button type="button" aria-label="How it works" onClick={() => onInfoClick && onInfoClick()} style={{ background: '#F4F1F8', border: '1px solid #EFEAF1', cursor: 'pointer', borderRadius: '50%', width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#6B6B73', padding: 0, flexShrink: 0, fontFamily: "'DM Sans', sans-serif" }}>i</button>
            </div>
            {/* plan_12: no day count or amount under the title */}
          </div>
          <div className="flex items-center flex-shrink-0 wy-month" style={{ height: 36, borderRadius: 999, background: '#FFFFFF', padding: '0 4px' }}>
            <button onClick={onPrevMonth} aria-label="Previous month" className="flex items-center justify-center" style={{ width: 30, height: 36, border: 0, background: 'none' }}>
              <ChevronLeft className="w-4 h-4" style={{ color: '#8B879A' }} />
            </button>
            <span style={{ fontSize: 14, fontWeight: 500, color: '#8B879A', minWidth: 28, textAlign: 'center' }}>{shortMonthName}</span>
            <button onClick={onNextMonth} aria-label="Next month" className="flex items-center justify-center" style={{ width: 30, height: 36, border: 0, background: 'none' }}>
              <ChevronRight className="w-4 h-4" style={{ color: '#8B879A' }} />
            </button>
          </div>
        </div>

        <div className="flex" style={{ background: '#ECE8F4', borderRadius: 999, padding: 3, marginTop: 14 }}>{/* plan_24: tighter */}
          <button type="button" onClick={onOverviewClick} style={segBtn(monthOn)}>This month</button>
          <button type="button" onClick={onNotesClick} style={segBtn(memosOn)}>Memos</button>
          <button type="button" onClick={() => (hasAccess('yearSummary') ? onStatsClick() : showFeaturePaywall('yearSummary'))} style={segBtn(statsOn)}>
            {!hasAccess('yearSummary') ? '🔒 ' : ''}{currentYear} stats
          </button>
        </div>

        {monthOn && (
          <>
            {showSetupBanner && (
              <button type="button" onClick={onMonthSetup} className="w-full flex items-center text-left" style={{ ...card, gap: 14, border: 0 }} data-testid="month-setup-banner">
                <span className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: '#FCF3D2', fontSize: 18 }}>{'🗓️'}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#26252E' }}>Set up {currentMonthName}</span>
                  <span style={{ display: 'block', fontSize: 13, color: '#8B879A', marginTop: 2 }}>Income, budgets, starting balances</span>
                </span>
                <span style={{ background: '#1F1E24', color: '#FFFFFF', borderRadius: 999, padding: '9px 16px', fontSize: 14, fontWeight: 600, flexShrink: 0 }}>Start</span>
              </button>
            )}

            {/* plan_11: one card - where the money went, then the pace */}
            <div style={{ ...card, borderRadius: 28, padding: '16px 16px 14px', marginTop: 12 }} data-testid="plan-month-path">
              <div className="flex items-start justify-between" style={{ gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <h2 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: '-0.3px', color: '#2B2C31' }}>
                    {currentMonthName}{isCurrentMonth ? ' so far' : ''} {'✨'}
                  </h2>
                  <p style={{ margin: '3px 0 0', fontSize: 13.5, color: '#7A7A82', lineHeight: 1.45 }}>Where your money went.</p>
                </div>
              </div>
              <div style={{ marginTop: -6 }}>
              <PlanPath
                flow
                steps={tiles.map((t, i) => ({
                  emoji: t.emoji,
                  label: ['Came in', 'Spent', 'Saved', 'Kept'][i] || t.label,
                  amount: compact(tileRaw[i]),
                  bg: t.bg,
                  amountBg: t.bg,
                  amountInk: t.ink,
                }))}
              />
              </div>
              {hasCarry && (
                <div style={{ background: '#F4F1FB', borderRadius: 22, padding: '13px 14px 12px', marginTop: 12 }} data-testid="plan-cash-left">
                  {/* dark_08: the same look as "Budget left to spend" */}
                  <span style={{ display: 'block', fontSize: 12.5, color: '#9A8FBF', fontWeight: 500 }}>Cash left</span>
                  <span style={{ display: 'block', fontSize: 18, fontWeight: 600, letterSpacing: '-0.4px', color: cashLeft < 0 ? '#C04B7C' : '#2B2C31', marginTop: 2, lineHeight: 1.2 }}>{formatCurrency(cashLeft)}</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A', marginTop: 4, lineHeight: 1.4 }}>
                    Kept {carryIn < 0 ? '−' : '+'} {formatCurrency(Math.abs(carryIn))} from {prevMonthName}
                  </span>
                </div>
              )}
              {monthlyBudget > 0 && (
                <div style={{ background: '#F4F1FB', borderRadius: 22, padding: '13px 14px 12px', marginTop: 12 }} data-testid="plan-left-to-spend">
                  {/* plan_12: left to spend first, then budget used and month gone */}
                  <div className="flex items-start" style={{ gap: 12 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 12.5, color: '#9A8FBF', fontWeight: 500 }}>{budgetAvailable < 0 ? 'Over budget by' : 'Budget left to spend'}</span>
                      <span style={{ display: 'block', fontSize: 18, fontWeight: 600, letterSpacing: '-0.4px', color: budgetAvailable < 0 ? '#C04B7C' : '#2B2C31', marginTop: 2, lineHeight: 1.2 }} data-testid="plan-left-line">
                        {formatCurrency(Math.abs(budgetAvailable))}
                      </span>
                    </span>
                    {paceVerdict && (
                      <span style={{ flexShrink: 0, borderRadius: 999, padding: '6px 11px', fontSize: 12.5, fontWeight: 600, background: good ? '#DFF2E7' : '#FAE4ED', color: good ? '#2F8756' : '#C04B7C' }}>{paceVerdict}</span>
                    )}
                  </div>
                  <div className="grid" style={{ gridTemplateColumns: isCurrentMonth ? '1fr 1fr' : '1fr', gap: 8, marginTop: 10 }}>
                    {[
                      ['Budget used', budgetSpentPercent, '#F2AFC8'],
                      ...(isCurrentMonth ? [['Month gone', monthProgress, '#B9A6F0']] : []),
                    ].map(([l, v, c]) => (
                      <div key={l} style={{ background: '#FFFFFF', borderRadius: 16, padding: '10px 12px' }}>
                        <div className="flex items-baseline justify-between" style={{ gap: 6 }}>
                          <span style={{ fontSize: 12, color: '#8B879A' }}>{l}</span>
                          <span style={{ fontSize: 15, fontWeight: 600, color: '#2B2C31' }}>{v}%</span>
                        </div>
                        <div style={{ height: 6, borderRadius: 9, background: '#F0EDF6', marginTop: 7, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(100, Math.max(0, v || 0))}%`, height: '100%', borderRadius: 9, background: c }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  // Abbreviate spent over $1k for the hero (3.5k, 12.4k, 1.2M)
  const formatSpentHero = (n) => {
    if (n >= 1000000) return '$' + (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1000) return '$' + (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    return '$' + Math.round(n).toLocaleString();
  };

  return (
    <div className="px-4 pt-4 pb-2 wy-budget-hero" style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* Purple: the header and the four tiles sit in the gradient band (wy-ph). */}
      <div className="wy-ph" style={{ '--ph-lift': '1rem' }}>
      {/* Floating header */}
      <div className="mb-3">
        {/* Title row — info "i" on the left, month-nav pill top-right */}
        <div className="flex items-center justify-between mb-3">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '28px', fontWeight: 400, color: '#1A1A1F', letterSpacing: '-0.6px' }}>
              {'Monthly Budget'}
            </h1>
            <button
              onClick={() => onInfoClick && onInfoClick()}
              style={{ background: '#F4F1F8', border: '1px solid #EFEAF1', cursor: 'pointer', borderRadius: '50%', width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#6B6B73', padding: 0, flexShrink: 0 }}
            >i</button>
          </div>
          <div className="flex items-center gap-0.5 wy-month">
            <button onClick={onPrevMonth} className="w-4 h-4 flex items-center justify-center">
              <ChevronLeft className="w-3.5 h-3.5" style={{ color: '#a39fa0' }} />
            </button>
            <span className="text-xs font-semibold px-1 min-w-[32px] text-center" style={{ color: '#a39fa0' }}>{shortMonthName}</span>
            <button onClick={onNextMonth} className="w-4 h-4 flex items-center justify-center">
              <ChevronRight className="w-3.5 h-3.5" style={{ color: '#a39fa0' }} />
            </button>
          </div>
        </div>

        {/* Tab row — Overview / Setup / Stats, underline style matching the wellness app's tab bar.
            wy-tabs / data-on: Purple turns these into chips (index.css). */}
        <div className="flex items-center justify-center wy-tabs" style={{ gap: '48px' }}>
          <button
            onClick={onOverviewClick}
            data-on={!showBudgetSetup && !showYearlyStats ? '1' : undefined}
            style={{ paddingBottom: '10px', fontSize: '14px', fontWeight: !showBudgetSetup && !showYearlyStats ? 700 : 500, color: !showBudgetSetup && !showYearlyStats ? '#1A1A1F' : '#a39fa0', position: 'relative', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            This Month
            {!showBudgetSetup && !showYearlyStats && (
              <span style={{ position: 'absolute', bottom: '-1px', left: '15%', right: '15%', height: '2px', background: accentColor, borderRadius: '2px' }} />
            )}
          </button>
          <button
            onClick={onNotesClick}
            data-on={showBudgetSetup ? '1' : undefined}
            style={{ paddingBottom: '10px', fontSize: '14px', fontWeight: showBudgetSetup ? 700 : 500, color: showBudgetSetup ? '#1A1A1F' : '#a39fa0', position: 'relative', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Memos
            {showBudgetSetup && (
              <span style={{ position: 'absolute', bottom: '-1px', left: '15%', right: '15%', height: '2px', background: accentColor, borderRadius: '2px' }} />
            )}
          </button>
          <button
            onClick={() => hasAccess('yearSummary') ? onStatsClick() : showFeaturePaywall('yearSummary')}
            data-on={showYearlyStats ? '1' : undefined}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', paddingBottom: '10px', fontSize: '14px', fontWeight: showYearlyStats ? 700 : 500, color: showYearlyStats ? '#1A1A1F' : '#a39fa0', position: 'relative', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {!hasAccess('yearSummary') && <Lock className="w-3 h-3" />}
            {currentYear} Stats
            {showYearlyStats && (
              <span style={{ position: 'absolute', bottom: '-1px', left: '15%', right: '15%', height: '2px', background: accentColor, borderRadius: '2px' }} />
            )}
          </button>
        </div>

      </div>

      {!showBudgetSetup && !showYearlyStats && (
      <>
        {/* Card 1: This month — Income / Spent / Saved/Inv. / Leftover — 2x2 solid-color tiles.
            No eyebrow here: the timeframe is already established by the "Monthly Budget" title
            and the Sep month-nav pill directly above, so a "This Month" label was redundant. */}
        {showSetupBanner && (
          <button
            type="button"
            onClick={onMonthSetup}
            className="w-full flex items-center gap-3 rounded-2xl mt-6 mb-5"
            style={{
              padding: '14px 15px',
              textAlign: 'left',
              // #D4157E rather than #e91e8c: see the patch header. The subtitle
              // cannot be read on the brighter pink at any size worth using.
              background: purple ? '#AC8DF2' : isPink ? '#D4157E' : isWarm ? '#2D5A3D' : '#4361EE',
              boxShadow: purple ? '0 8px 20px -12px rgba(120,90,190,0.45)' : isPink
                ? '0 10px 22px -12px rgba(212,21,126,0.65)'
                : isWarm
                  ? '0 10px 22px -12px rgba(45,90,61,0.60)'
                  : '0 10px 22px -12px rgba(67,97,238,0.60)',
            }}
            data-testid="month-setup-banner"
          >
            <span
              className="rounded-full flex items-center justify-center flex-shrink-0"
              style={{ width: '36px', height: '36px', background: '#FFFFFF' }}
            >
              <svg
                width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke={purple ? '#AC8DF2' : isPink ? '#D4157E' : isWarm ? '#2D5A3D' : '#4361EE'}
                strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              >
                <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" />
              </svg>
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: '19px', fontWeight: 700, letterSpacing: '-0.2px', color: '#FFFFFF' }}>
                Set up {currentMonthName}
              </span>
              <span style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: '#FFFFFF', marginTop: '1px' }}>
                Income, budgets, starting balances
              </span>
            </span>
            <ChevronRight className="w-[18px] h-[18px] flex-shrink-0" style={{ color: 'rgba(255,255,255,0.9)' }} />
          </button>
        )}

        <div className={`grid grid-cols-2 gap-2 mb-3${showSetupBanner ? '' : ' mt-6'}`}>
          <div className="rounded-[22px] wy-tile" style={{ background: tilePalette[0].bg, padding: '12px 12px 11px' }}>
            <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="1" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '14px', marginBottom: '10px' }}>💰</div>
            <span className="wy-tile-label" style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tilePalette[0].label, marginBottom: '2px' }}>Income</span>
            <p className="wy-tile-value" style={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', color: tilePalette[0].value }}>{formatCurrency(totalIncome)}</p>
          </div>
          <div className="rounded-[22px] wy-tile" style={{ background: tilePalette[1].bg, padding: '12px 12px 11px' }}>
            <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="2" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '14px', marginBottom: '10px' }}>🛍️</div>
            <span className="wy-tile-label" style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tilePalette[1].label, marginBottom: '2px' }}>Spent</span>
            <p className="wy-tile-value" style={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', color: tilePalette[1].value }}>{formatCurrency(totalSpent)}</p>
          </div>
          <div className="rounded-[22px] wy-tile" style={{ background: tilePalette[2].bg, padding: '12px 12px 11px' }}>
            <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="3" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '14px', marginBottom: '10px' }}>🐷</div>
            <span className="wy-tile-label" style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tilePalette[2].label, marginBottom: '2px' }}>Saved/Inv.</span>
            <p className="wy-tile-value" style={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', color: totalSavings < 0 ? '#B91C1C' : tilePalette[2].value }}>{formatCurrency(totalSavings)}</p>
          </div>
          <div className="rounded-[22px] wy-tile" style={{ background: tilePalette[3].bg, padding: '12px 12px 11px' }}>
            <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="4" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '14px', marginBottom: '10px' }}>📉</div>
            <span className="wy-tile-label" style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tilePalette[3].label, marginBottom: '2px' }}>Leftover</span>
            <p className="wy-tile-value" style={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', color: leftover < 0 ? '#B91C1C' : tilePalette[3].value }}>{formatCurrency(leftover)}</p>
          </div>
        </div>
      </>
      )}
      </div>

      {!showBudgetSetup && !showYearlyStats && (
      <>
        {/* Pace card — now uses the SAME gradient tile treatment as Income/Spent/Saved/
            Leftover above it (tilePalette[0], the Income token) so it reads as part of
            that top group instead of the plain white cards below. Month elapsed uses
            tilePalette[3] (Leftover's purple/lavender token) instead so the two rings
            are distinguishable at a glance; Budget spent stays on tilePalette[0] (pink)
            to match the card's own background family. Top row split into two flex:1
            columns divided by a hairline (mirrors Available), with a matching divider
            added to the left of "On pace" so both rows in the card use the same
            construction. Ring holes now render the SAME gradient as the card background
            (instead of solid white) so they blend into the tile instead of reading as a
            punched-out hole revealing the app's outer page colour. Ring tracks and both
            dividers switched from grey to translucent white to sit correctly on a
            coloured card. "Month elapsed" label gets whiteSpace: nowrap to guarantee it
            stays on one line at this card width. */}
        <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2 mt-6" style={{ color: labelColor }}><span className="wy-star">✦ </span>Pace</p>
        <div className="rounded-2xl wy-tile" style={{ padding: purple ? '11px 14px' : '13px 14px', background: pace.bg, ...(purple ? { boxShadow: 'none', border: '1px solid #F1EDF4' } : {}) }}>
          <div className={`flex items-center ${purple ? 'mb-2' : 'mb-3'}`}>
            <div className="flex items-center" style={{ flex: 1 }}>
              <div style={{ fontSize: purple ? '13px' : '14px', color: pace.day, fontWeight: 700 }}>Day {currentDay} of {daysInMonth}</div>
            </div>
            <div style={{ width: '1px', height: '20px', alignSelf: 'center', background: pace.line, margin: '0 14px' }} />
            <div className="flex items-center" style={{ flex: 1 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 700, color: paceTextColor, background: paceBgColor, padding: '3px 9px', borderRadius: '999px' }}>
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: paceDotColor, display: 'inline-block' }}></span>
                {paceVerdict}
              </span>
            </div>
          </div>

          <div className="flex items-stretch">
            {/* Month elapsed — lavender/purple ring (Leftover token), distinct from the
                card's own pink so the two rings are easy to tell apart at a glance. */}
            <div className="flex items-center gap-2" style={{ flex: 1, minWidth: 0 }}>
              <div style={{ position: 'relative', width: purple ? '38px' : '50px', height: purple ? '38px' : '50px', borderRadius: '50%', flexShrink: 0, background: `conic-gradient(${pace.month || paceMonthRing} ${monthProgress}%, ${pace.track || paceTrack} ${monthProgress}% 100%)` }}>
                <div style={{ position: 'absolute', inset: purple ? '4px' : '5px', borderRadius: '50%', background: pace.bg }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: purple ? '12px' : '13px', fontWeight: 600, color: purple ? pace.label : tilePalette[3].value, marginBottom: '1px', whiteSpace: 'nowrap' }}>Month elapsed</div>
                <div style={{ fontSize: purple ? '14px' : '15px', fontWeight: 700, color: purple ? pace.value : tilePalette[3].value }}>{monthProgress}%</div>
              </div>
            </div>

            <div style={{ width: '1px', height: purple ? '38px' : '50px', alignSelf: 'center', background: pace.line, margin: '0 12px' }} />

            {/* Budget spent — pink ring (Income token), matches the card's own family. */}
            <div className="flex items-center gap-2" style={{ flex: 1, minWidth: 0 }}>
              <div style={{ position: 'relative', width: purple ? '38px' : '50px', height: purple ? '38px' : '50px', borderRadius: '50%', flexShrink: 0, background: `conic-gradient(${pace.spent} ${Math.min(100, budgetSpentPercent)}%, ${pace.track || paceTrack} ${Math.min(100, budgetSpentPercent)}% 100%)` }}>
                <div style={{ position: 'absolute', inset: purple ? '4px' : '5px', borderRadius: '50%', background: pace.bg }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: purple ? '12px' : '13px', fontWeight: 600, color: purple ? pace.label : pace.spent, marginBottom: '1px', whiteSpace: 'nowrap' }}>Budget spent</div>
                <div style={{ fontSize: purple ? '14px' : '15px', fontWeight: 700, color: purple ? pace.value : pace.spent }}>{budgetSpentPercent}%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Available — Budget / Money, side-by-side columns divided by a hairline.
            Eyebrow matches Budget Progress/Payment Methods/Breakdown exactly (13px, same
            Tailwind spacing classes) — it was 14px with custom inline margins before, which
            is why it read larger than the others. */}
        <>
          <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2 mt-6" style={{ color: labelColor }}><span className="wy-star">✦ </span>Available</p>
          <div className="rounded-2xl bg-white border border-gray-100 shadow-sm mb-3 flex" style={{ padding: '14px 0' }}>
            {/* Budget column — label above value, own column width to wrap into for long
                currency values (e.g. NGN) instead of colliding with the Money column. */}
            <div className="flex items-center gap-2" style={{ flex: 1, padding: '0 14px', minWidth: 0 }}>
              <div className="rounded-lg flex items-center justify-center" style={{ width: '36px', height: '36px', background: purple ? '#F6F4F8' : tilePalette[4].bg, fontSize: '16px', flexShrink: 0 }}>📊</div>
              <div style={{ minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', fontSize: '12.5px', fontWeight: 500, color: purple ? '#857D93' : '#1F2937', marginBottom: '1px', lineHeight: 1.3 }}>Budget left to spend</span>
                <p style={{ fontSize: '15px', fontWeight: 700, letterSpacing: '-0.2px', lineHeight: 1.2, wordBreak: 'break-word', color: budgetAvailable < 0 ? '#B91C1C' : purple ? '#5E35A8' : tilePalette[4].value }}>{formatCurrency(budgetAvailable)}</p>
              </div>
            </div>

            <div style={{ width: '1px', height: '40px', alignSelf: 'center', background: '#F0EDF2', margin: '0' }} />

            {/* Money column */}
            <div className="flex items-center gap-2" style={{ position: 'relative', flex: 1, padding: '0 14px', minWidth: 0 }} ref={moneyTooltipRef}>
              <div className="rounded-lg flex items-center justify-center" style={{ width: '36px', height: '36px', background: purple ? '#F6F4F8' : tilePalette[5].bg, fontSize: '16px', flexShrink: 0 }}>💵</div>
              <div style={{ minWidth: 0 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12.5px', fontWeight: 500, color: purple ? '#857D93' : '#1F2937', marginBottom: '1px', lineHeight: 1.3 }}>
                  Money
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setShowMoneyTooltip(v => !v); }}
                    style={{ background: '#F4F1F8', border: '1px solid #EFEAF1', cursor: 'pointer', borderRadius: '50%', width: '14px', height: '14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700, color: tilePalette[5].label, padding: 0, flexShrink: 0, lineHeight: 1 }}
                    aria-label="What is Money Available?"
                  >i</button>
                </span>
                <p style={{ fontSize: '15px', fontWeight: 700, letterSpacing: '-0.2px', lineHeight: 1.2, wordBreak: 'break-word', color: purple ? '#1E1630' : tilePalette[5].value }}>{formatCurrency(cashAvailable)}</p>
              </div>
              {showMoneyTooltip && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  marginTop: '6px',
                  width: '240px',
                  maxWidth: 'calc(100vw - 32px)',
                  background: '#FFFFFF',
                  color: '#1A1A1F',
                  border: '1px solid #EFEAF1',
                  borderRadius: '10px',
                  padding: '10px 12px',
                  fontSize: '11px',
                  lineHeight: 1.45,
                  fontWeight: 500,
                  boxShadow: '0 6px 20px rgba(0,0,0,0.10)',
                  zIndex: 50,
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  The total of your debit card and cash balances, minus any money owed on your credit cards.
                </div>
              )}
            </div>
          </div>
        </>
      </>
      )}

    </div>
  );
};

export default BudgetHero;
